import { BASE_URL, appConfig } from '../../apiConfig';
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Eye, EyeClosed, Mail, Lock, User, Home, Phone, Banknote, Store, Image,
  ArrowLeft, ArrowRight, ChevronDown, CheckCircle, Loader2, Info, XCircle,
  CalendarDays,
  ShoppingBag,
  Plane,
  LineChart,
  Box,
  Euro,
  Boxes,
} from "lucide-react";
import BankSelect from './components/BankSelect';

const InputField = ({ id, label, type = "text", value, onChange, icon: Icon, placeholder, required, children, error, className = "", ...props }) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-xs font-semibold text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />}
      {children || (
        <input
          type={type}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          className={`w-full ${Icon ? 'pr-11' : 'pr-4'} pl-4 py-3 border ${error ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 dark:text-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none ${className}`}
          placeholder={placeholder}
          required={required}
          {...props}
        />
      )}
    </div>
    {error && (
      <p className="text-red-500 text-xs mt-1 flex items-center">
        <Info className="w-3 h-3 ml-1 inline-block text-xs" /> {error}
      </p>
    )}
  </div>
);

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);




  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    nameStore: "",
    nameBank: "",
    rib: "",
    address: "",
    phone: "",
    level: "",
  });



  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "rib" ? value.replace(/\D/g, "").slice(0, 24) : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  const handleLogoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogo(null);
      setLogoPreview(null);
    }
    setErrors(prev => ({ ...prev, logo: undefined }));
  }, []);

  const clearLogo = useCallback(() => {
    setLogo(null);
    setLogoPreview(null);
    setErrors(prev => ({ ...prev, logo: undefined }));
  }, []);

  const validateStep = useCallback((step) => {
    let newErrors = {};
    
    if (step === 1) {
      if (!formData.name) newErrors.name = t('auth.name_required');
      if (!formData.email) {
        newErrors.email = t('auth.email_required');
      } else if (!/\S+.\S+\.\S+/.test(formData.email)) {
        newErrors.email = t('auth.email_invalid');
      }
      if (!formData.password) {
        newErrors.password = t('auth.password_required');
      } else if (formData.password.length < 6) {
        newErrors.password = t('auth.password_min_length');
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('auth.confirm_password_required');
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('auth.password_mismatch');
      }
    } else if (step === 2) {
      if (!formData.nameBank) newErrors.nameBank = t('auth.bank_required');
      if (!formData.rib) {
        newErrors.rib = t('auth.rib_required');
      } else if (!/^\d{24}$/.test(formData.rib)) {
        newErrors.rib = t('auth.rib_24_digits');
      }
      if (!formData.address) newErrors.address = t('auth.address_required');
      if (!formData.phone) newErrors.phone = t('auth.phone_required');
      if (!formData.level) newErrors.level = t('auth.level_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleNextStep = () => {
    const isValid = validateStep(currentStep);
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError("");
    setShowSuccessModal(false);

    const isStep2Valid = validateStep(2);
    if (!isStep2Valid) {
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("password", formData.password);
      formDataToSend.append("password_confirmation", formData.confirmPassword);
      formDataToSend.append("role", 0);
      formDataToSend.append("name_store", formData.nameStore);
      formDataToSend.append("name_bank", formData.nameBank);
      formDataToSend.append("rib", formData.rib);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("level", formData.level);
      if (logo) {
        formDataToSend.append("logo", logo);
      }

      const response = await fetch(BASE_URL + "/api/register", {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          setErrors(data.errors);
          const hasStep1Errors = Object.keys(data.errors).some(key => 
            ['name', 'email', 'password', 'confirmPassword'].includes(key)
          );
          if (hasStep1Errors) {
            setCurrentStep(1);
          }
          setGeneralError(t('auth.register_validation_failed'));
        } else {
          setGeneralError(data.message || t('auth.register_creation_failed'));
        }
        return;
      }

      setShowSuccessModal(true);
      setTimeout(() => {
        navigate("/login");
      }, 7000);
    } catch (err) {
      console.error("Registration error:", err);
      setGeneralError(t('auth.server_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-gradient-to-br from-[var(--auth-bg-from)] via-[var(--auth-bg-via)] to-[var(--auth-bg-to)] flex items-center justify-center p-4 sm:p-8"
    >
      <div className="w-full max-w-6xl bg-gradient-to-br from-[var(--auth-card-from)] to-[var(--auth-card-to)] rounded-[2rem] shadow-2xl overflow-hidden grid md:grid-cols-2 border border-[var(--color-primary-100)]">
        {/* ===== Form side ===== */}
        <div className="flex flex-col justify-center px-8 sm:px-12 py-8">
          <div className="">
            <span className="inline-flex items-center rounded-full bg-gradient-to-r from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] px-4 py-1.5 text-xs font-medium text-gray-700">
              {t('auth.my_platform')}
            </span>
          </div>

          <div className="">
           <div className="text-center mb-3">
             <div className="mx-auto w-12 h-12 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
               <Lock className="w-6 h-6 text-white" />
             </div>
             <h1 className="text-xl font-bold text-gray-900 mb-2">{t('auth.register_title')}</h1>
             <p className="text-gray-600 text-xs">
                {t('auth.register_step', { step: currentStep })}
                {currentStep === 1 ? t('auth.step1_title') : t('auth.step2_title')}
              </p>
           </div>

           <div className="relative pt-1">
             <div className="overflow-hidden h-1.5 mb-4 text-xs flex rounded bg-[var(--color-primary-100)]">
               <div style={{ width: currentStep === 1 ? "50%" : "100%" }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[var(--color-primary-500)] transition-all duration-300 ease-in-out"></div>
             </div>
           </div>

           <form onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }} className="space-y-6">
             {generalError && (
              <div className="text-red-600 bg-red-100 border border-red-300 px-4 py-1.5 rounded-lg text-xs text-center">
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="inline-block ml-2 w-4 h-4" />
                  {generalError}
                </div>
              </div>
             )}
             

             {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('auth.account_info_title')}</h2>
                <InputField
                  id="name"
                  label={t('auth.full_name_label')}
                  icon={User}
                  value={formData.name}
                  name="name"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder={t('auth.full_name_placeholder')}
                  required
                  error={errors.name}
                />

                <InputField
                  id="email"
                  label={t('auth.email_label')}
                  type="email"
                  icon={Mail}
                  value={formData.email}
                  name="email"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder={t('auth.email_placeholder')}
                  required
                  error={errors.email}
                />

                <div className="flex gap-3 items-center justify-between">
                  <InputField
                    id="password"
                    label={t('auth.password_label')}
                    type={showPassword ? "text" : "password"}
                    icon={Lock}
                    value={formData.password}
                    name="password"
                    onChange={handleChange}
                    placeholder={t('auth.password_placeholder')}
                    required
                    error={errors.password}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`pr-11 w-full pl-12 py-3 text-xs border ${errors.password ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 dark:text-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none`}
                       placeholder={t('auth.password_placeholder')}
                       required
                     />
                  </InputField>
                  <InputField
                    id="confirmPassword"
                    label={t('auth.confirm_password_label')}
                    type={showPassword ? "text" : "password"}
                    icon={Lock}
                    value={formData.confirmPassword}
                    name="confirmPassword"
                    onChange={handleChange}
                    placeholder={t('auth.confirm_password_placeholder')}
                    required
                    error={errors.confirmPassword}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`pr-11 w-full pl-12 text-xs py-3 border ${errors.confirmPassword ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 dark:text-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none`}
                      placeholder={t('auth.confirm_password_placeholder')}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute cursor-pointer left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <Eye className="w-4 h-4" /> : <EyeClosed className="w-4 h-4" />}
                    </button>
                  </InputField>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('auth.seller_info_title')}</h2>

                <InputField
                  id="nameStore"
                  label={t('auth.store_name_label')}
                  icon={Store}
                  value={formData.nameStore}
                  name="nameStore"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder={t('auth.store_name_placeholder')}
                  error={errors.name_store}
                />

                <BankSelect 
                    type={true}
                    formData={formData} 
                    handleChange={handleChange} 
                    errors={errors} 
                  />

                <InputField
                  id="rib"
                  label={t('auth.rib_label')}
                  icon={Banknote}
                  value={formData.rib}
                  name="rib"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder={t('auth.rib_placeholder')}
                  required
                  inputMode="numeric"
                  maxLength={24}
                  error={errors.rib}
                />

                <InputField
                  id="address"
                  label={t('auth.address_label')}
                  icon={Home}
                  value={formData.address}
                  name="address"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder={t('auth.address_placeholder')}
                  required
                  error={errors.address}
                />

                <InputField
                  id="phone"
                  label={t('auth.phone_label')}
                  type="tel"
                  icon={Phone}
                  value={formData.phone}
                  name="phone"
                  onChange={handleChange}
                  placeholder={t('auth.phone_placeholder')}
                  required
                  className= {`${ isRTL ? 'text-right' : ''} text-xs`}
                  error={errors.phone}
                />

                <div>
                  <label htmlFor="level" className="block text-xs font-semibold text-gray-700 mb-2">
                    {t('auth.seller_level_label')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      className={`w-full text-xs cursor-pointer pr-4 pl-4 py-3 border ${errors.level ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 dark:text-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none appearance-none`}
                      required
                    >
                      <option value="">{t('auth.seller_level_placeholder')}</option>
                      <option value="Beginner">{t('auth.level_beginner')}</option>
                      <option value="Intermediate">{t('auth.level_intermediate')}</option>
                      <option value="Advanced">{t('auth.level_advanced')}</option>
                    </select>
                    <ChevronDown size={15} className="absolute top-3.5 left-2 text-gray-500" />

                    {errors.level && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <Info className="w-3 h-3 ml-1 inline-block" /> {errors.level}
                      </p>
                    )}
                  </div>
                </div>

                <div className="">
                  <label htmlFor="logo" className="block text-xs font-semibold text-gray-700 mb-2">
                    {t('auth.store_logo_label')}
                  </label>
                  <div className="relative border border-gray-200 rounded-xl bg-gray-50 focus-within:ring-2 focus-within:ring-[var(--color-primary-200)]">
                    <input
                      type="file"
                      id="logo"
                      name="logo"
                      onChange={handleLogoChange}
                      className="w-full cursor-pointer text-xs dark:text-white/60 pr-11 pl-4 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary-50)] file:text-[var(--color-primary-500)] hover:file:bg-[var(--color-primary-100)]"
                      accept="image/*"
                    />
                    <Image className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                  {errors.logo && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <Info className="w-3 h-3 ml-1 inline-block" /> {errors.logo}
                    </p>
                  )}

                  {logoPreview && (
                    <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                      <div className="flex items-center">
                        <img src={logoPreview} alt="Logo Preview" className="h-16 w-16 object-contain rounded-lg mr-4" />
                        <span className="text-sm text-gray-700">{logo ? logo.name : ''}</span>
                      </div>
                      <button
                        type="button"
                        onClick={clearLogo}
                        className="text-red-500 cursor-pointer hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition-colors"
                        aria-label="Remove logo"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between gap-4 mt-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="flex-1 text-xs cursor-pointer bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-300 focus:ring-4 focus:ring-gray-200 transition-all duration-200 flex items-center justify-center group"
                  disabled={loading}
                >
                  {
                    isRTL ? (
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:-translate-x-[-3px] transition-transform duration-200" />
                    ) : (
                      <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-[-4px] transition-transform duration-200" />
                    )
                  }
                  {t('auth.back_button')}
                </button>
              )}

              {currentStep < 2 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className={`flex-1 cursor-pointer text-xs bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] text-white py-3 px-4 rounded-xl font-semibold hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] focus:ring-4 focus:ring-blue-200 transition-all duration-200 flex items-center justify-center group shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin ml-2 w-4 h-4" /> : null}
                  {t('auth.next_button')}
                  {
                    isRTL ? (
                      <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                    ) : (
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:-translate-x-[-3px] transition-transform duration-200" />
                    )
                  }
                </button>
              ) : (
                <button
                  type="submit"
                  className={`flex-1 text-xs cursor-pointer bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] text-white py-3 px-4 rounded-xl font-semibold hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-400)] focus:ring-4 focus:ring-blue-200 transition-all duration-200 flex items-center justify-center group shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin ml-2 w-4 h-4" /> : null}
                  {t('auth.submit_button')}
                  {
                    isRTL ? (
                      <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                    ) : (
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:-translate-x-[-3px] transition-transform duration-200" />
                    )
                  }
                </button>
              )}
            </div>
          </form>

          <div className="mt-2 text-center">
            <p className="text-gray-600 text-xs">
              {t('auth.has_account')}{" "}
              <Link to="/login" className="text-[var(--color-primary-500)] hover:text-[var(--color-primary-400)] font-semibold">
                {t('auth.login_link_text')}
              </Link>
            </p>
          </div>
        </div>
        </div>

        {/* ===== Decorative side ===== */}
        <div className="hidden md:block relative m-3 rounded-[1.6rem] overflow-hidden">
          <img
            src={ appConfig.slug === "nextlead" ?
              "https://images.unsplash.com/photo-1603195827187-459ab02554a0?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                :
              "https://images.unsplash.com/photo-1556565681-306458ef93cd?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            }
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* Gradient overlays */}
          <div className="absolute -top-16 -left-16 w-72 h-72 bg-gradient-to-br from-[var(--color-primary-200)] to-[var(--color-primary-50)] opacity-30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tr from-[var(--color-primary-300)] to-[var(--color-primary-100)] opacity-20 rounded-full blur-3xl" />
          
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/20" />

          {/* Top Stats Card - Today's Earnings */}
          <div className="absolute top-6 right-4 flex gap-4">
            <div className=" bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-sm text-white rounded-2xl px-4 py-3 shadow-xl w-56 border border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold">{t('auth.today_earnings')}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)] animate-pulse" />
              </div>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-bold">1,247MAD+</span>
                <span className="text-[10px] text-green-300 flex items-center gap-0.5">
                  ↑ 12.5%
                </span>
              </div>
              <span className="text-[10px] text-white/60">{t('auth.products_sold')}</span>
            </div>
            {/* Weekly Growth Indicator */}
            <div className="bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/10">
              <div className="text-center">
                <span className="text-[10px] text-white/60 block">{t('auth.weekly_growth')}</span>
                <span className="text-lg font-bold text-green-300">24.8%+</span>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <div className="w-1 h-4 bg-green-300/30 rounded-full" />
                  <div className="w-1 h-6 bg-green-300/50 rounded-full" />
                  <div className="w-1 h-8 bg-green-300 rounded-full" />
                  <div className="w-1 h-5 bg-green-300/50 rounded-full" />
                  <div className="w-1 h-3 bg-green-300/30 rounded-full" />
                </div>
              </div>
            </div>

          </div>

          {/* Monthly Revenue Card */}
          <div className="absolute top-36 right-4 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-sm text-white rounded-2xl px-4 py-2 shadow-xl w-44 border border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">إجمالي الأرباح</span>
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                <LineChart className="w-4 h-4 text-white" />
              </div>
            </div>
            <span className="text-lg font-bold">38,000MAD+</span>
          </div>

          {/* Stats Row - Products & Commissions */}
          <div className="absolute bottom-8 left-8 right-8 flex gap-3">
            <div className="flex-1 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-white/60">إجمالي المنتجات</span>
                  <p className="text-lg font-bold text-white">2,000+</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                  <ShoppingBag className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
            
            <div className="flex-1 bg-gradient-to-br from-white/25 to-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-white/60">العمولات</span>
                  <p className="text-lg font-bold text-white">8,432MAD</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-400)]/30 flex items-center justify-center">
                  <Boxes className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Floating Stats - Weekly Progress */}
          <div className="absolute top-1/2 left-6 -translate-y-1/2 flex flex-col gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <ShoppingBag className="w-3.5 h-3.5" />
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <LineChart className="w-3.5 h-3.5" />
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-white/30 to-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xs font-semibold hover:scale-110 transition-transform duration-300 cursor-default">
              <Boxes className="w-3.5 h-3.5" />
            </div>
            
            {/* Progress indicator */}
            <div className="w-0.5 h-12 bg-gradient-to-b from-white/40 to-white/10 mx-auto my-1" />
            <div className="text-[8px] text-white/40 text-center -rotate-90 whitespace-nowrap">
              هذا الأسبوع
            </div>
          </div>

          
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" dir={isRTL ? "rtl" : "ltr"}>
          <div className="w-full max-w-sm bg-white rounded-[2rem] shadow-2xl border border-[var(--color-primary-100)] p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[var(--color-primary-500)] mb-2">{t('auth.register_success_title')}</h3>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">{t('auth.register_success_wait')}</p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('auth.register_redirecting')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}